import React, { useState, useEffect } from 'react';
import { apiFetch } from '../context/AuthContext';
import { ColorBadge } from '../components/ProfileDisplay';

export default function AdminPage() {
  const [tab, setTab] = useState('users');
  const [users, setUsers]   = useState([]);
  const [codes, setCodes]   = useState([]);
  const [teams, setTeams]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user' });
  const [newCode, setNewCode] = useState({ note: '', uses_remaining: -1 });
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [resetPw, setResetPw]   = useState({});
  const [editTeam, setEditTeam] = useState(null); // {id, name, description, members:[]}
  const [allUsers, setAllUsers] = useState([]);   // for member picker

  function flash(type, text) { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); }

  async function loadAll() {
    setLoading(true);
    const [u, c, t] = await Promise.all([
      apiFetch('/admin/users').catch(() => []),
      apiFetch('/admin/codes').catch(() => []),
      apiFetch('/teams').catch(() => []),
    ]);
    setUsers(u); setCodes(c); setTeams(t); setAllUsers(u);
    setLoading(false);
  }
  useEffect(() => { loadAll(); }, []);

  // ── User actions ────────────────────────────────────────
  async function createUser(e) {
    e.preventDefault();
    try {
      await apiFetch('/admin/users', { method: 'POST', body: newUser });
      flash('success', `Benutzer ${newUser.email} angelegt.`);
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      loadAll();
    } catch (err) { flash('error', err.message); }
  }
  async function toggleActive(u) {
    await apiFetch(`/admin/users/${u.id}`, { method: 'PATCH', body: { active: !u.active } });
    loadAll();
  }
  async function changeRole(u, role) {
    await apiFetch(`/admin/users/${u.id}`, { method: 'PATCH', body: { role } });
    loadAll();
  }
  async function doResetPw(userId) {
    const pw = resetPw[userId];
    if (!pw || pw.length < 8) { flash('error', 'Passwort mind. 8 Zeichen'); return; }
    await apiFetch(`/admin/users/${userId}`, { method: 'PATCH', body: { password: pw } });
    setResetPw(r => ({ ...r, [userId]: '' }));
    flash('success', 'Passwort zurückgesetzt.');
  }

  // ── Code actions ────────────────────────────────────────
  async function createCode(e) {
    e.preventDefault();
    try {
      const data = await apiFetch('/admin/codes', { method: 'POST', body: newCode });
      flash('success', `Code erstellt: ${data.code}`);
      setNewCode({ note: '', uses_remaining: -1 });
      loadAll();
    } catch (err) { flash('error', err.message); }
  }
  async function deactivateCode(id) {
    await apiFetch(`/admin/codes/${id}`, { method: 'DELETE' }); loadAll();
  }

  // ── Team actions ────────────────────────────────────────
  async function createTeam(e) {
    e.preventDefault();
    try {
      await apiFetch('/teams', { method: 'POST', body: newTeam });
      flash('success', `Team "${newTeam.name}" angelegt.`);
      setNewTeam({ name: '', description: '' });
      loadAll();
    } catch (err) { flash('error', err.message); }
  }

  async function openEditTeam(team) {
    const members = await apiFetch(`/teams/${team.id}/members`).catch(() => []);
    const memberIds = members.map(m => m.id);
    setEditTeam({ ...team, memberIds });
  }

  async function saveTeamMembers() {
    try {
      await apiFetch(`/teams/${editTeam.id}/members`, { method: 'PUT', body: { userIds: editTeam.memberIds } });
      if (editTeam.name !== teams.find(t => t.id === editTeam.id)?.name || editTeam.description !== teams.find(t => t.id === editTeam.id)?.description) {
        await apiFetch(`/teams/${editTeam.id}`, { method: 'PATCH', body: { name: editTeam.name, description: editTeam.description } });
      }
      flash('success', 'Team gespeichert.');
      setEditTeam(null);
      loadAll();
    } catch (err) { flash('error', err.message); }
  }

  async function toggleTeamActive(team) {
    await apiFetch(`/teams/${team.id}`, { method: 'PATCH', body: { active: !team.active } }); loadAll();
  }

  function toggleMember(userId) {
    setEditTeam(prev => {
      const ids = prev.memberIds.includes(userId)
        ? prev.memberIds.filter(id => id !== userId)
        : [...prev.memberIds, userId];
      return { ...prev, memberIds: ids };
    });
  }

  const set = (obj, setObj) => k => e => setObj(prev => ({ ...prev, [k]: e.target.value }));

  const TABS = [['users','👥 Benutzer'],['teams','🏷 Teams'],['codes','🔑 Registrierungscodes']];

  return (
    <div className="page-wide">
      <h1 style={{ fontFamily: 'Georgia,serif', fontSize: '1.8rem', fontWeight: 600, marginBottom: 4 }}>Administration</h1>
      <p style={{ color: '#555', marginBottom: 24 }}>Benutzer, Teams und Registrierungscodes verwalten.</p>

      {msg.text && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#E8E5DF', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {TABS.map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '9px 20px', border: 'none', borderRadius: 7, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer',
              background: tab === t ? '#1C1C1C' : 'transparent',
              color: tab === t ? '#fff' : '#555', transition: 'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── USERS ── */}
      {tab === 'users' && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-hd"><div><div className="sub">Neuer Benutzer</div><h2>Benutzer anlegen</h2></div></div>
            <div className="card-body">
              <form onSubmit={createUser}>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Name</label>
                    <input className="form-input" value={newUser.name} onChange={set(newUser,setNewUser)('name')} placeholder="Max Mustermann" required /></div>
                  <div className="form-group"><label className="form-label">E-Mail</label>
                    <input className="form-input" type="email" value={newUser.email} onChange={set(newUser,setNewUser)('email')} placeholder="max@firma.de" required /></div>
                  <div className="form-group"><label className="form-label">Passwort</label>
                    <input className="form-input" type="password" value={newUser.password} onChange={set(newUser,setNewUser)('password')} placeholder="Mind. 8 Zeichen" required /></div>
                  <div className="form-group"><label className="form-label">Rolle</label>
                    <select className="form-select" value={newUser.role} onChange={set(newUser,setNewUser)('role')}>
                      <option value="user">Benutzer</option>
                      <option value="admin">Administrator</option>
                    </select></div>
                </div>
                <button className="btn btn-primary" type="submit">Benutzer anlegen</button>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-hd"><div><div className="sub">{users.length} Benutzer</div><h2>Alle Benutzer</h2></div></div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead><tr>
                  <th>Name</th><th>E-Mail</th><th>Rolle</th><th>Profil</th><th>Status</th><th>Passwort</th><th>Aktion</th>
                </tr></thead>
                <tbody>
                  {loading && <tr><td colSpan={7} style={{ textAlign:'center', color:'#888', padding:24 }}>Laden…</td></tr>}
                  {users.map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight:600 }}>{u.name}</td>
                      <td style={{ color:'#555', fontSize:'0.88rem' }}>{u.email}</td>
                      <td>
                        <select value={u.role} onChange={e => changeRole(u, e.target.value)}
                          style={{ border:'1.5px solid #AAA', borderRadius:6, padding:'4px 8px', fontSize:'0.82rem', background:'#fff', cursor:'pointer' }}>
                          <option value="user">Benutzer</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td><ColorBadge color={u.dominant_color} size="sm" /></td>
                      <td>
                        <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:100, fontSize:'0.78rem', fontWeight:700,
                          background: u.active ? '#E6F4EE' : '#F4F4F4', color: u.active ? '#165C38' : '#888',
                          border: `1.5px solid ${u.active ? '#80C0A0' : '#CCC'}` }}>
                          {u.active ? 'Aktiv' : 'Deaktiviert'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          <input type="password" placeholder="Neues PW" value={resetPw[u.id]||''}
                            onChange={e => setResetPw(r => ({ ...r, [u.id]: e.target.value }))}
                            style={{ width:120, padding:'4px 8px', border:'1.5px solid #AAA', borderRadius:6, fontSize:'0.82rem' }} />
                          <button className="btn btn-outline btn-sm" onClick={() => doResetPw(u.id)}>✓</button>
                        </div>
                      </td>
                      <td>
                        <button className={`btn btn-sm ${u.active ? 'btn-danger' : 'btn-outline'}`} onClick={() => toggleActive(u)}>
                          {u.active ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── TEAMS ── */}
      {tab === 'teams' && (
        <>
          {/* Edit overlay */}
          {editTeam && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
              <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:600, maxHeight:'90vh', overflow:'auto', padding:32 }}>
                <h2 style={{ fontFamily:'Georgia,serif', fontSize:'1.3rem', fontWeight:600, marginBottom:20 }}>Team bearbeiten</h2>
                <div className="form-group"><label className="form-label">Name</label>
                  <input className="form-input" value={editTeam.name} onChange={e => setEditTeam(t => ({ ...t, name: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Beschreibung</label>
                  <input className="form-input" value={editTeam.description||''} onChange={e => setEditTeam(t => ({ ...t, description: e.target.value }))} placeholder="Optional" /></div>

                <div style={{ fontSize:'0.78rem', fontWeight:700, letterSpacing:'0.09em', textTransform:'uppercase', color:'#333', marginBottom:12, marginTop:4 }}>
                  Mitglieder ({editTeam.memberIds.length} ausgewählt)
                </div>

                <div style={{ maxHeight:300, overflow:'auto', border:'2px solid #CCC', borderRadius:8 }}>
                  {allUsers.filter(u => u.active).map((u, i) => {
                    const isMember = editTeam.memberIds.includes(u.id);
                    return (
                      <label key={u.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px',
                        borderBottom: i < allUsers.length-1 ? '1px solid #EEE' : 'none',
                        background: isMember ? '#EAF0FA' : '#fff', cursor:'pointer', transition:'background 0.12s' }}>
                        <input type="checkbox" checked={isMember} onChange={() => toggleMember(u.id)}
                          style={{ width:16, height:16, cursor:'pointer' }} />
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:600, fontSize:'0.95rem', color:'#111' }}>{u.name}</div>
                          <div style={{ fontSize:'0.78rem', color:'#777' }}>{u.email}</div>
                        </div>
                        {u.dominant_color && <ColorBadge color={u.dominant_color} size="sm" />}
                      </label>
                    );
                  })}
                </div>

                <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
                  <button className="btn btn-outline" onClick={() => setEditTeam(null)}>Abbrechen</button>
                  <button className="btn btn-primary" onClick={saveTeamMembers}>Speichern</button>
                </div>
              </div>
            </div>
          )}

          {/* Create team */}
          <div className="card" style={{ marginBottom:20 }}>
            <div className="card-hd"><div><div className="sub">Neues Team</div><h2>Team anlegen</h2></div></div>
            <div className="card-body">
              <form onSubmit={createTeam}>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Teamname</label>
                    <input className="form-input" value={newTeam.name} onChange={set(newTeam,setNewTeam)('name')} placeholder="z.B. Vertrieb" required /></div>
                  <div className="form-group"><label className="form-label">Beschreibung (optional)</label>
                    <input className="form-input" value={newTeam.description} onChange={set(newTeam,setNewTeam)('description')} placeholder="Kurze Beschreibung" /></div>
                </div>
                <button className="btn btn-primary" type="submit">Team erstellen</button>
              </form>
            </div>
          </div>

          {/* Teams table */}
          <div className="card">
            <div className="card-hd"><div><div className="sub">{teams.length} Teams</div><h2>Alle Teams</h2></div></div>
            <div style={{ overflowX:'auto' }}>
              <table className="data-table">
                <thead><tr><th>Name</th><th>Beschreibung</th><th>Mitglieder</th><th>Status</th><th>Aktionen</th></tr></thead>
                <tbody>
                  {loading && <tr><td colSpan={5} style={{ textAlign:'center', color:'#888', padding:24 }}>Laden…</td></tr>}
                  {teams.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontWeight:700 }}>{t.name}</td>
                      <td style={{ color:'#555', fontSize:'0.88rem' }}>{t.description||'—'}</td>
                      <td>
                        <span style={{ fontWeight:700 }}>{t.member_count}</span>
                        <span style={{ color:'#888', fontSize:'0.82rem', marginLeft:4 }}>Mitglieder</span>
                      </td>
                      <td>
                        <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:100, fontSize:'0.78rem', fontWeight:700,
                          background: t.active ? '#E6F4EE' : '#F4F4F4', color: t.active ? '#165C38' : '#888',
                          border: `1.5px solid ${t.active ? '#80C0A0' : '#CCC'}` }}>
                          {t.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td style={{ display:'flex', gap:8 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openEditTeam(t)}>✏ Bearbeiten</button>
                        <button className={`btn btn-sm ${t.active ? 'btn-danger' : 'btn-outline'}`} onClick={() => toggleTeamActive(t)}>
                          {t.active ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && teams.length===0 && <tr><td colSpan={5} style={{ textAlign:'center', color:'#888', padding:24 }}>Noch keine Teams erstellt.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── CODES ── */}
      {tab === 'codes' && (
        <>
          <div className="card" style={{ marginBottom:20 }}>
            <div className="card-hd"><div><div className="sub">Neuer Code</div><h2>Registrierungscode erstellen</h2></div></div>
            <div className="card-body">
              <p style={{ fontSize:'0.9rem', color:'#555', marginBottom:16 }}>
                Mit einem Registrierungscode können sich Nutzer selbst anmelden. „Unbegrenzt" bedeutet, dass beliebig viele Personen denselben Code nutzen können.
              </p>
              <form onSubmit={createCode}>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Bezeichnung (optional)</label>
                    <input className="form-input" value={newCode.note} onChange={set(newCode,setNewCode)('note')} placeholder="z.B. Onboarding April" /></div>
                  <div className="form-group"><label className="form-label">Maximale Verwendungen</label>
                    <select className="form-select" value={newCode.uses_remaining} onChange={e => setNewCode(n => ({ ...n, uses_remaining: parseInt(e.target.value) }))}>
                      <option value={-1}>Unbegrenzt</option>
                      <option value={1}>1 ×</option><option value={5}>5 ×</option>
                      <option value={10}>10 ×</option><option value={25}>25 ×</option><option value={50}>50 ×</option>
                    </select></div>
                </div>
                <button className="btn btn-primary" type="submit">Code generieren</button>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-hd"><div><div className="sub">{codes.length} Codes</div><h2>Registrierungscodes</h2></div></div>
            <div style={{ overflowX:'auto' }}>
              <table className="data-table">
                <thead><tr><th>Code</th><th>Bezeichnung</th><th>Verwendungen</th><th>Erstellt</th><th>Status</th><th>Aktion</th></tr></thead>
                <tbody>
                  {codes.map(c => (
                    <tr key={c.id}>
                      <td><code style={{ background:'#F0EDE8', padding:'4px 10px', borderRadius:6, fontWeight:700, fontSize:'0.95rem', letterSpacing:'0.08em', border:'1.5px solid #CCC' }}>{c.code}</code></td>
                      <td style={{ color:'#555' }}>{c.note||'—'}</td>
                      <td>{c.uses_remaining===-1 ? '∞ Unbegrenzt' : `${c.uses_remaining} verbleibend`}</td>
                      <td style={{ fontSize:'0.82rem', color:'#777' }}>{new Date(c.created_at).toLocaleDateString('de-DE')}</td>
                      <td>
                        <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:100, fontSize:'0.78rem', fontWeight:700,
                          background: c.active ? '#E6F4EE' : '#F4F4F4', color: c.active ? '#165C38' : '#888',
                          border: `1.5px solid ${c.active ? '#80C0A0' : '#CCC'}` }}>
                          {c.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td>{c.active && <button className="btn btn-danger btn-sm" onClick={() => deactivateCode(c.id)}>Deaktivieren</button>}</td>
                    </tr>
                  ))}
                  {!loading && codes.length===0 && <tr><td colSpan={6} style={{ textAlign:'center', color:'#888', padding:24 }}>Noch keine Codes erstellt.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
